import React,{ useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from './templates/DefaultLayout';
import { useHistory } from 'react-router';
import { useAuth, useForm } from '~/hooks';
import FormGroup from '~/components/form/form-group';
import { getFirmById } from '~/api/lawfirm';
import { isEmpty } from 'lodash';
import { auth } from '~/api';
import { FormPassword } from '~/components/form';
import { auth as authUtil, stringUtils } from '~/util';

const initialState = {
    firstName: '',
    lastName: '',
    title: {},
    emailAddress: '',
    initialUserName: '',
    userName: '',
    password: '',
    confirmPassword: '',
    newPassword: '',
    firmData: {}
};

const EditProfile = () => {
    const { user, reloadUser } = useAuth();
    const { 
        id, 
        firstName,
        lastName,
        firmId, 
        email, 
        preferences = {},
        username
    } = user;

    const history = useHistory();
   
    const onSubmit = async ({ values, onFail, onInvalidForm, resetFormErrors }) => {
        try {
            resetFormErrors();
            let errors = {};
            if (!values.userName) errors.userName = 'Username is required';
            if (values.userName.length < 5) errors.userName = 'Username must have a minimum of 5 characters';
            if (!values.emailAddress) errors.emailAddress = 'Email address is required';
            
            const hasNewPassword = stringUtils.hasValue(values.newPassword);
            
            if (hasNewPassword) {
                if (!values.password) 
                    errors.password = 'Password is required';
                if (values.newPassword !== values.confirmPassword) 
                    errors.confirmPassword = 'New Password and Confirm password should match';                
            }

            if (!isEmpty(errors)) return onInvalidForm(errors);

            const payload = {
                firstName: values.firstName,
                lastName: values.lastName,
                emailAddress: values.emailAddress,
                userName: values.initialUserName,
            };
            
            if(values.initialUserName !== values.userName){
                payload.newUserName = values.userName;
            }
            
            if (hasNewPassword) {
                payload.password = values.password;
                payload.newPassword = values.newPassword;
            }

            const response = await auth.updateUserDetails(id, payload);

            if(!response || response.errored){
                errors.formError = 'Failed to update user profile';
                return onInvalidForm(errors);
            }else{
                await reloadUser();
                history.push('/');
            }
        } catch (e) {
            return onFail(e.message);
        }
    };

    const onLoad = useCallback(async (values) => {
        let firmData = {};
        if (firmId != null) {
            const firm = await getFirmById(firmId);

            firmData.firmName   = firm.Name;
            firmData.address    = firm.Address1;
            firmData.stateName  = firm.State;
            firmData.city       = firm.City;
        }

        return {
            ...values,
            firstName,
            lastName,
            title: {
                label: preferences.title,
                value: preferences.title
            },
            emailAddress: email,
            userName: username,
            initialUserName: username,
            password:'',
            confirmPassword:'',
            newPassword:  '',
            firmData: { ...firmData }
        };
    }, [id, firstName, lastName, username, email, firmId, preferences]);

    const {
        state,
        formActions,
        fieldActions
    } = useForm({
        initialState,
        onSubmit,
        onLoad
    });

    const hasErrors = Object.keys(state.errors)
        .filter(e => e != 'formError')
        .length > 0;

    const { firmData } = state.values;
    
    return (
        <Layout title="Edit Profile" subtitle="">
            <div className="container flex items-stretch justify-between flex-col py-4">
                <div className="grid grid-cols-12 grid-flow-row gap-8">
                    <FormGroup
                        label="First Name"
                        labelClassName="text-sm font-medium" 
                        placeholder="First Name"
                        path="firstName"
                        groupClassName='col-span-6'
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.firstName}
                        error={state.errors.firstName}
                        {...fieldActions}
                    />
                    <FormGroup
                        label="Last Name"
                        labelClassName="text-sm font-medium"
                        placeholder="Last Name"
                        path="lastName"
                        groupClassName='col-span-6'
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.lastName}
                        error={state.errors.lastName}
                        {...fieldActions}
                    />
                    <FormGroup
                        label="Email Address"
                        labelClassName="text-sm font-medium"   
                        placeholder="Email Address"
                        path="emailAddress"
                        groupClassName='col-span-6'
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.emailAddress}
                        error={state.errors.emailAddress}
                        {...fieldActions}
                    />
                    <FormGroup
                        label="Username"
                        labelClassName="text-sm font-medium"  
                        placeholder="Username"
                        path="userName"
                        groupClassName='col-span-6'
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={state.values.userName}
                        error={state.errors.userName}
                        {...fieldActions}
                        onBlur={async (value, path) => {
                            const usernameTaken = await auth.isUsernameTaken(value);

                            const throwIfTaken = () => {
                                if (usernameTaken)
                                    return `'${value}' is already in use`;
                                return true;                            
                            };

                            return fieldActions.onBlur(value, path, throwIfTaken);
                        }}
                    />
                    <FormGroup
                        label="Title"
                        labelClassName="text-sm font-medium" 
                        readOnly 
                        path="title"
                        groupClassName='col-span-6'
                        inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                        value={preferences.title}
                    />
                    {firmId != null && (
                        <>
                            <FormGroup
                                label="Firm Name"
                                labelClassName="text-sm font-medium" 
                                readOnly 
                                path="firmName"
                                groupClassName='col-span-6'
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={firmData.firmName}
                            />
                            <FormGroup
                                label="Address"
                                labelClassName="text-sm font-medium" 
                                readOnly 
                                path="address"
                                groupClassName='col-span-6'
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={firmData.address}
                            />
                            <FormGroup
                                label="City"
                                labelClassName="text-sm font-medium"  
                                path="city"
                                readOnly
                                groupClassName='col-span-3'
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={firmData.city}
                            />
                            <FormGroup
                                label="State"
                                labelClassName="text-sm font-medium" 
                                readOnly
                                path="state"
                                groupClassName='col-span-3'
                                inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                                value={firmData.stateName}
                            />
                        </>
                    )}
                    <div className='col-span-12 -mb-4 mt-4'>
                        <p className='font-semibold'>Change Password</p>
                    </div>
                    <FormPassword
                        label="Current Password" 
                        path="password"
                        groupClassName='col-span-6'
                        labelClassName="text-sm" 
                        value={state.values.password}
                        error={state.errors.password}   
                        {...fieldActions}
                    />
                    <FormPassword 
                        label="New Password"
                        path="newPassword"
                        groupClassName='col-span-6 col-start-1'
                        labelClassName="text-sm"
                        value={state.values.newPassword}
                        error={state.errors.newPassword}
                        validator={value=> {
                            if(value){
                                return authUtil.isStrongPassword(value) ? true : 'Password must include atleast 8 characters, atleast one digit, and atleast one upper case character';
                            }
                            return true;
                        }}  
                        {...fieldActions}
                        onChange={(value, path) => {
                            fieldActions.onChange('', 'confirmPassword');
                            fieldActions.onChange(value, path);                            
                        }}
                    />
                    <FormPassword 
                        label="Confirm New Password"
                        path="confirmPassword"
                        groupClassName='col-span-6'
                        labelClassName="text-sm" 
                        value={state.values.confirmPassword}
                        error={state.errors.confirmPassword}
                        validator={value => state.values.newPassword === value || 'Password confirmation does not match'}
                        {...fieldActions}
                    />
                </div>
               
                {state.errors.formError && (
                    <div className="my-4">
                        <p className="text-red-400 font-medium ml-2">{state.errors.formError}</p>
                    </div>
                )}
                <div className="flex items-center justify-end mt-8">
                    <Link to="/" className="ml-3 py-2 px-4 rounded-full text-blueGray-100 bg-gray-100 font-semibold">
                        Cancel
                    </Link>
                    <button 
                        disabled={hasErrors}
                        className="ml-4 py-2 px-4 text-white font-medium rounded-full bg-lightGreen-300 disabled:opacity-50" 
                        onClick={formActions.submit}
                    >
                        Update
                    </button>
                </div>
            </div>
        </Layout>
    );
};

export default EditProfile;